import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  ArrowLeft,
  Calendar,
  Gauge,
  Fuel,
  Cog,
  Palette,
  Zap,
  Phone,
  Mail,
  Loader2,
  ShieldCheck,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import useSeo from "@/lib/useSeo";
import useJsonLd from "@/lib/useJsonLd";
import useRecentlyViewed from "@/lib/useRecentlyViewed";
import RecentlyViewed from "@/components/RecentlyViewed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FUEL_SCHEMA = {
  Benzina: "https://schema.org/Gasoline",
  Diesel: "https://schema.org/Diesel",
  Ibrido: "https://schema.org/HybridFuel",
  Elettrico: "https://schema.org/ElectricFuel",
  GPL: "https://schema.org/LPGFuel",
  Metano: "https://schema.org/BiodieselFuel",
};

export default function VehicleDetail() {
  const { id } = useParams();
  const { track } = useRecentlyViewed();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useSeo(
    vehicle
      ? {
          title: `${vehicle.brand} ${vehicle.model} ${
            vehicle.type === "scooter_cabinato" ? "Scooter Cabinato" : "Usato"
          } | Minicarmobilità (MI)`,
          description: `${vehicle.brand} ${vehicle.model} - ${vehicle.year}, ${new Intl.NumberFormat(
            "it-IT"
          ).format(vehicle.km)} km, ${vehicle.fuel}. € ${new Intl.NumberFormat(
            "it-IT"
          ).format(vehicle.price)}. Garanzia 12 mesi inclusa. Vetrina a Zibido San Giacomo (MI).`,
        }
      : {
          title: "Dettaglio veicolo | Minicarmobilità",
          description: "Dettagli del veicolo in vetrina presso Minicarmobilità.",
        }
  );

  // Schema.org Vehicle JSON-LD — pure metadata, zero visual impact.
  const ldData = vehicle
    ? (() => {
        const origin =
          typeof window !== "undefined" ? window.location.origin : "";
        const absolutize = (u) => {
          if (!u) return u;
          if (/^https?:\/\//i.test(u)) return u;
          if (u.startsWith("/")) {
            const backend = process.env.REACT_APP_BACKEND_URL || origin;
            return `${backend}${u}`;
          }
          return u;
        };
        const isScooter = vehicle.type === "scooter_cabinato";
        const node = {
          "@context": "https://schema.org",
          "@type": isScooter ? "Motorcycle" : "Car",
          name: `${vehicle.brand} ${vehicle.model}`,
          brand: { "@type": "Brand", name: vehicle.brand },
          model: vehicle.model,
          vehicleModelDate: String(vehicle.year),
          productionDate: String(vehicle.year),
          mileageFromOdometer: {
            "@type": "QuantitativeValue",
            value: vehicle.km,
            unitCode: "KMT",
          },
          fuelType: FUEL_SCHEMA[vehicle.fuel] || vehicle.fuel,
          itemCondition: "https://schema.org/UsedCondition",
          image: (vehicle.images || []).map(absolutize).filter(Boolean),
          description: vehicle.description || `${vehicle.brand} ${vehicle.model} ${vehicle.year}`,
          url: origin ? `${origin}/vehicle/${vehicle.id}` : undefined,
          offers: {
            "@type": "Offer",
            price: vehicle.price,
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/UsedCondition",
            url: origin ? `${origin}/vehicle/${vehicle.id}` : undefined,
            seller: {
              "@type": "AutoDealer",
              name: "Minicarmobilità",
              telephone: "+393297452439",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Via Puccini 5",
                addressLocality: "Zibido San Giacomo",
                addressRegion: "MI",
                postalCode: "20058",
                addressCountry: "IT",
              },
            },
          },
        };
        if (vehicle.color) node.color = vehicle.color;
        if (vehicle.transmission)
          node.vehicleTransmission = vehicle.transmission;
        if (vehicle.power) node.vehicleEngine = { "@type": "EngineSpecification", enginePower: vehicle.power };
        return node;
      })()
    : null;

  useJsonLd(vehicle ? `vehicle-${vehicle.id}` : null, ldData);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/vehicles/${id}`)
      .then((res) => {
        if (!mounted) return;
        setVehicle(res.data);
        if (res.data?.id) track(res.data.id);
      })
      .catch(() => mounted && setVehicle(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id, track]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        vehicle_id: id,
        message: `[Richiesta per ${vehicle?.brand} ${vehicle?.model}] ${form.message}`,
      });
      toast.success("Richiesta inviata! Ti contatteremo a breve.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Errore nell'invio. Riprova o chiamaci.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 flex items-center justify-center text-[#7a7f77]">
          <Loader2 className="animate-spin" />
          <span className="ml-3">Caricamento veicolo…</span>
        </div>
      </main>
    );
  }

  if (!vehicle) {
    return (
      <main className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 max-w-3xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl text-[#1c1f1a] mb-4">Veicolo non trovato</h1>
          <p className="text-[#7a7f77] mb-8">Il veicolo che cerchi non è più disponibile.</p>
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#5ab500] text-white font-bold"
          >
            <ArrowLeft size={16} /> Torna alla vetrina
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const fmtPrice = new Intl.NumberFormat("it-IT").format(vehicle.price);
  const fmtKm = new Intl.NumberFormat("it-IT").format(vehicle.km);
  const images = vehicle.images?.length
    ? vehicle.images
    : ["https://images.pexels.com/photos/170811/pexels-photo-170811.jpeg?auto=compress&cs=tinysrgb&w=1600"];

  const specs = [
    { icon: Calendar, label: "Anno", value: vehicle.year },
    { icon: Gauge, label: "Chilometri", value: `${fmtKm} km` },
    { icon: Fuel, label: "Alimentazione", value: vehicle.fuel },
    { icon: Cog, label: "Cambio", value: vehicle.transmission || "—" },
    { icon: Zap, label: "Potenza", value: vehicle.power || "—" },
    { icon: Palette, label: "Colore", value: vehicle.color || "—" },
  ];

  const waMessage = encodeURIComponent(
    `Salve, sono interessato a ${vehicle.brand} ${vehicle.model} (€ ${fmtPrice}). Posso avere maggiori informazioni?`
  );

  return (
    <main data-testid="vehicle-detail-page" className="min-h-screen bg-white">
      <Header />

      <div className="pt-28 pb-16 max-w-7xl mx-auto px-6 md:px-10">
        <Link
          to="/veicoli"
          data-testid="back-link"
          className="inline-flex items-center gap-2 text-sm text-[#7a7f77] hover:text-[#5ab500] transition mb-8"
        >
          <ArrowLeft size={14} /> Torna alla vetrina
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <div className="relative rounded-3xl overflow-hidden card-soft border border-black/[0.05] aspect-[4/3]">
              <img
                src={images[activeImg]}
                alt={`${vehicle.brand} ${vehicle.model}`}
                data-testid="vehicle-main-image"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <span
                  className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider backdrop-blur-md shadow ${
                    vehicle.type === "scooter_cabinato"
                      ? "bg-[#5ab500] text-white"
                      : "bg-white/95 text-[#1c1f1a]"
                  }`}
                >
                  {vehicle.type === "scooter_cabinato" ? "Scooter Cabinato" : "Auto Usata"}
                </span>
              </div>
            </div>

            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3" data-testid="thumbnails">
                {images.map((img, i) => (
                  <button
                    key={`${img}-${i}`}
                    onClick={() => setActiveImg(i)}
                    data-testid={`thumb-${i}`}
                    className={`relative rounded-xl overflow-hidden aspect-[4/3] border-2 transition ${
                      i === activeImg
                        ? "border-[#5ab500]"
                        : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt={`thumb ${i}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-7 border border-black/[0.05] shadow-[var(--shadow-card)]">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#7a7f77] font-bold mb-2">
                {vehicle.brand}
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-light text-[#1c1f1a] leading-tight">
                {vehicle.model}
              </h1>

              <div className="mt-6 pb-6 border-b border-black/[0.08]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#7a7f77] font-bold mb-1">
                  Prezzo
                </div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-[#5ab500]">
                  € {fmtPrice}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4">
                {specs.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#f5f6f4] flex items-center justify-center flex-shrink-0">
                      <Icon size={14} className="text-[#5ab500]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-[#7a7f77] font-bold">
                        {label}
                      </div>
                      <div className="text-sm text-[#1c1f1a] font-medium truncate">
                        {value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex items-center gap-2 px-4 py-3 rounded-xl bg-[#5ab500]/8 border border-[#5ab500]/20">
                <ShieldCheck size={16} className="text-[#5ab500]" />
                <span className="text-xs text-[#1c1f1a] font-medium">
                  Garanzia inclusa · Tagliandi certificati
                </span>
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={`https://wa.me/393297452439?text=${waMessage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-whatsapp-btn"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#25D366] text-white font-bold hover:shadow-[0_8px_24px_rgba(37,211,102,0.4)] transition"
                >
                  <MessageCircle size={16} fill="white" /> Scrivi su WhatsApp
                </a>
                <a
                  href="tel:+393297452439"
                  data-testid="detail-call-btn"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#1c1f1a] text-white font-bold hover:bg-[#5ab500] transition"
                >
                  <Phone size={16} /> 329 745 2439
                </a>
                <a
                  href="mailto:auto.licari26@gmail.com"
                  data-testid="detail-email-btn"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-black/15 text-[#1c1f1a] hover:border-[#5ab500] hover:text-[#5ab500] transition"
                >
                  <Mail size={16} /> Scrivici una mail
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <h2 className="font-heading text-2xl md:text-3xl font-light text-[#1c1f1a] mb-5">
              Descrizione
            </h2>
            <p className="text-[#4a4f47] leading-relaxed whitespace-pre-line">
              {vehicle.description || "Nessuna descrizione disponibile."}
            </p>
          </div>

          <div className="lg:col-span-2">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-[#f5f6f4] rounded-3xl p-7 border border-black/[0.05]"
            >
              <h3 className="font-heading text-xl text-[#1c1f1a] mb-1 font-semibold">
                Richiedi informazioni
              </h3>
              <p className="text-xs text-[#7a7f77] mb-5">Ti rispondiamo entro 24 ore.</p>

              <div className="space-y-3">
                <input
                  type="text" required placeholder="Nome e cognome"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                />
                <input
                  type="email" required placeholder="Email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
                <input
                  type="tel" placeholder="Telefono (opzionale)"
                  data-testid="contact-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={inputCls}
                />
                <textarea
                  required rows={4} placeholder="Sono interessato a questo veicolo..."
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={inputCls + " resize-none"}
                />
                <button
                  type="submit" disabled={sending}
                  data-testid="contact-submit"
                  className="w-full px-6 py-3.5 rounded-xl bg-[#5ab500] text-white font-bold hover:bg-[#4a9a00] hover:shadow-[0_8px_24px_rgba(90,181,0,0.4)] disabled:opacity-60 transition-all"
                >
                  {sending ? "Invio in corso..." : "Invia richiesta"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <RecentlyViewed excludeId={id} />

      <Footer />
    </main>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-black/[0.08] text-sm text-[#1c1f1a] placeholder-[#7a7f77] focus:border-[#5ab500] outline-none transition";
